/**
 * Tradução entre a checagem de prenhez como a tela a usa e a linha de
 * `dg_checks` como o banco a guarda.
 *
 * Existe porque os dois lados divergiram e ninguém percebeu: a aba escrevia
 * `transfer_id`, `recipient_id` e `sexing: { sex, confidence }`, e o hook lia
 * `te_transfer_id`, `recipient_animal_id`, `fetus_sex` e `sexing_confidence`.
 * Nenhum casava, em nenhuma das duas direções — então a receptora, a
 * transferência e o SEXO DO FETO iam para o banco como NULL, sem erro nenhum,
 * e voltavam vazios ao recarregar.
 *
 * Juntar ida e volta num arquivo só é o que torna isso testável: o teste de
 * ida-e-volta falha no instante em que um nome de campo divergir de novo.
 *
 * TRÊS CAMPOS CONTINUAM SENDO DESCARTADOS, e é de propósito por enquanto:
 * `reschedule_date`, `placentomes_count` e `abort_data` não têm coluna em
 * `dg_checks`. A tela os coleta e eles se perdem — como já acontecia. Criar as
 * colunas é um passo separado, para este aqui não misturar conserto com
 * mudança de schema.
 */

/** Checagem da tela → linha do banco. */
export const checkParaLinha = (check = {}, { laboratoryId = null, sortOrder = 0 } = {}) => ({
  laboratory_id: laboratoryId,
  sort_order: sortOrder,
  te_transfer_id: check.transfer_id || null,
  recipient_id: check.recipient_id || null,
  recipient_identifier: check.recipient_identifier || null,
  embryo_id: check.embryo_id || null,
  embryo_number: check.embryo_number || null,
  te_date: check.te_date || null,
  expected_date: check.expected_date || null,
  result: check.result || null,
  viability: check.viability != null ? check.viability : null,
  heartbeat: check.heartbeat || null,
  size_mm: check.size_mm || null,
  empty_cause: check.empty_cause || null,
  inconclusive_reason: check.inconclusive_reason || null,
  // Sexagem não realizada não grava sexo. Desmarcar a caixa é afirmar que não
  // se sexou; deixar o sexo gravado ali contradiria a própria marcação.
  fetus_sex: check.sexing?.performed ? (check.sexing.sex || null) : null,
  sexing_confidence: check.sexing?.performed ? (check.sexing.confidence || null) : null,
  observations: check.observations || null,
});

/** Linha do banco → checagem da tela. */
export const linhaParaCheck = (linha = {}) => ({
  _db_id: linha.id,
  id: linha.id,
  transfer_id: linha.te_transfer_id || null,
  recipient_id: linha.recipient_id || null,
  recipient_identifier: linha.recipient_identifier || null,
  embryo_id: linha.embryo_id || null,
  embryo_number: linha.embryo_number || null,
  te_date: linha.te_date || null,
  expected_date: linha.expected_date || null,
  result: linha.result || null,
  viability: linha.viability != null ? linha.viability : null,
  heartbeat: linha.heartbeat || null,
  size_mm: linha.size_mm || null,
  empty_cause: linha.empty_cause || null,
  inconclusive_reason: linha.inconclusive_reason || null,
  // `performed` não tem coluna: a existência do sexo É a marca de que se sexou.
  sexing: {
    performed: !!linha.fetus_sex,
    sex: linha.fetus_sex || null,
    confidence: linha.sexing_confidence || null,
  },
  observations: linha.observations || null,
});

/**
 * Esta DG tem algo que mereça ir para o banco?
 *
 * A gravação de uma DG TROCA a lista de checagens inteira: apaga o que está lá
 * e insere o que veio. Mandar uma DG vazia não é gravar nada, é APAGAR o que
 * existia. Foi assim que sessões nasceram no banco com zero checagens — a
 * DG60, ainda em branco, era gravada junto da DG30 que tinha resultado.
 *
 * Conteúdo é qualquer uma das três: alguém conferiu (`checks`), alguém marcou
 * como concluída, ou alguém preencheu o cabeçalho (data, técnico, método). O
 * cabeçalho conta porque marcar a data da conferência antes de sair a campo é
 * uso legítimo — o que não é legítimo é a tela gravar por iniciativa própria
 * uma etapa em que ninguém tocou.
 */
export const dgTemConteudo = (dg) => {
  if (!dg) return false;
  if ((dg.checks || []).length > 0) return true;
  if (dg.completed) return true;
  const s = dg.session || {};
  return !!(s.date || s.technician_id || s.technician_name || s.method);
};
